/*
 * Een foto verkleinen vóór het uploaden: hooguit 800 px (of `max`), als WebP (of JPEG
 * waar de browser geen WebP kan maken). Via een <img>, zodat ook een
 * HEIC-foto van een iPhone werkt. Geeft base64 terug, zonder "data:...,".
 */
export async function verklein(bestand: File, max = 800): Promise<string> {
  if (!bestand.type.startsWith('image/') && !/\.(heic|heif)$/i.test(bestand.name)) throw new Error('Kies een foto.')
  const url = URL.createObjectURL(bestand)
  try {
    const img = await new Promise<HTMLImageElement>((goed, fout) => {
      const i = new Image()
      i.onload = () => goed(i)
      i.onerror = () => fout(new Error('Deze foto kan de browser niet openen. Probeer een JPEG of PNG.'))
      i.src = url
    })
    const schaal = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight))
    const doek = document.createElement('canvas')
    doek.width = Math.max(1, Math.round(img.naturalWidth * schaal))
    doek.height = Math.max(1, Math.round(img.naturalHeight * schaal))
    const ctx = doek.getContext('2d')
    if (!ctx) throw new Error('Verkleinen lukt niet in deze browser.')
    ctx.drawImage(img, 0, 0, doek.width, doek.height)
    const maak = (type: string, kwaliteit: number) => new Promise<Blob | null>((r) => doek.toBlob(r, type, kwaliteit))
    /* Onder de grens van de server (600 kB) blijven: zo nodig een stap minder kwaliteit. */
    const GRENS = 560 * 1024
    let blob: Blob | null = null
    for (const q of [0.82, 0.7, 0.58, 0.46]) {
      blob = await maak('image/webp', q)
      if (!blob || blob.type !== 'image/webp') blob = await maak('image/jpeg', q + 0.03)
      if (!blob || blob.size <= GRENS) break
    }
    if (!blob) throw new Error('Verkleinen lukt niet in deze browser.')
    if (blob.size > GRENS) throw new Error('Deze foto blijft te groot. Kies een kleinere.')
    const data = await new Promise<string>((goed, fout) => {
      const r = new FileReader()
      r.onload = () => goed(String(r.result))
      r.onerror = () => fout(new Error('Lezen lukte niet.'))
      r.readAsDataURL(blob as Blob)
    })
    return data.replace(/^data:[^,]*,/, '')
  } finally {
    URL.revokeObjectURL(url)
  }
}
